import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Breadcrumb,
  Table,
  Button,
  Modal,
  Select,
  Input,
  Space,
  Tag,
  Badge,
  Form,
  Popconfirm,
  Drawer,
  Tabs,
  Alert,
  Spin,
  message,
} from 'antd'
import {
  PlusOutlined,
  TeamOutlined,
  CloudUploadOutlined,
  AppstoreOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'

import { useAuthStore } from '@/store/auth'
import {
  useAccessGroups,
  useAccessGroup,
  useCreateGroup,
  useUpdateGroup,
  useDeactivateGroup,
  useAddMember,
  useRemoveMember,
  useGroupEntries,
  useAddEntry,
  useDeleteEntry,
  useAccessConfigStatus,
  useDeployAccessConfig,
} from '@/hooks/useAccessGroups'
import { useOrganizations } from '@/hooks/useUsers'
import { useUsers } from '@/hooks/useUsers'
import type { AccessGroupResponse, AccessGroupMember, AccessGroupEntry } from '@/api/accessGroups'
import DatalensEntryPicker, { type DatalensEntrySelection } from '@/components/DatalensEntryPicker'
import { isOrgMembershipRoleKey } from '@/utils/accessGroups'

dayjs.locale('ru')

const { Title, Text } = Typography

/** Extract human-readable name from a DataLens key/entry_id (last segment after '/') */
function extractNameFromKey(key: string): string {
  const parts = key.split('/')
  return parts[parts.length - 1] || key
}

export default function AccessGroupsPage() {
  const navigate = useNavigate()
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'

  // ── Filters ──
  const [orgFilter, setOrgFilter] = useState<string | undefined>(
    isSuperadmin ? undefined : (authUser?.orgId ?? undefined)
  )

  // ── Modals & Drawer state ──
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<AccessGroupResponse | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false)
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false)
  const [selectedEntriesToAdd, setSelectedEntriesToAdd] = useState<DatalensEntrySelection[]>([])
  // deployModalOpen removed — sync is now inline Popconfirm + message feedback

  // ── Picker loading state ──
  const [pickerLoading, setPickerLoading] = useState(false)
  const [pendingMemberRemovals, setPendingMemberRemovals] = useState<Set<string>>(new Set())
  const [pendingMemberAdditions, setPendingMemberAdditions] = useState<AccessGroupMember[]>([])
  const [applyingMembers, setApplyingMembers] = useState(false)

  // ── Forms ──
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const [addMemberForm] = Form.useForm()

  // ── Data hooks ──
  const { data: groups, isLoading } = useAccessGroups(orgFilter)
  const { data: groupDetail, isLoading: detailLoading } = useAccessGroup(selectedGroupId ?? '')
  const { data: organizations } = useOrganizations()
  const { data: usersData } = useUsers({ page: 1, page_size: 200 })
  const { data: entries, isLoading: entriesLoading } = useGroupEntries(selectedGroupId ?? '')
  const { data: configStatus } = useAccessConfigStatus()
  // configDiff removed — deploy modal replaced with inline Popconfirm

  // ── Mutations ──
  const createGroupMutation = useCreateGroup()
  const updateGroupMutation = useUpdateGroup()
  const deactivateGroupMutation = useDeactivateGroup()
  const addMemberMutation = useAddMember()
  const removeMemberMutation = useRemoveMember()
  const addEntryMutation = useAddEntry()
  const deleteEntryMutation = useDeleteEntry()
  const deployMutation = useDeployAccessConfig()

  // ── Computed options ──
  const orgOptions = useMemo(() => {
    return (organizations || []).map((o) => ({
      value: o.id,
      label: `${o.code} — ${o.name}`,
    }))
  }, [organizations])

  const userOptions = useMemo(() => {
    const members = groupDetail?.members || []
    const memberIds = new Set(members.map((m) => m.user_id))
    const pendingAddIds = new Set(pendingMemberAdditions.map((m) => m.user_id))
    return (usersData?.items || [])
      .filter((u) => !memberIds.has(u.id) && !pendingAddIds.has(u.id) && u.is_active)
      .map((u) => ({
        value: u.id,
        label: `${u.full_name} (${u.email})`,
        full_name: u.full_name,
        email: u.email,
      }))
  }, [usersData, groupDetail, pendingMemberAdditions])

  const mergedMembers = useMemo(() => {
    const current = [...(groupDetail?.members || [])]
    const memberIds = new Set(current.map((m) => m.user_id))
    for (const member of pendingMemberAdditions) {
      if (!memberIds.has(member.user_id)) {
        current.push(member)
      }
    }
    return current
  }, [groupDetail, pendingMemberAdditions])

  const hasMemberChanges = pendingMemberRemovals.size > 0 || pendingMemberAdditions.length > 0

  const orgName = (orgId: string) => {
    const org = (organizations || []).find((o) => o.id === orgId)
    return org ? `${org.code} — ${org.name}` : orgId
  }
  const selectedGroupIsOrgMembership = isOrgMembershipRoleKey(groupDetail?.zitadel_role_key)

  // ── Handlers ──
  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields()
      await createGroupMutation.mutateAsync({
        name: values.name,
        zitadel_role_key: values.zitadel_role_key,
        description: values.description || null,
        org_id: values.org_id || null,
      })
      setCreateModalOpen(false)
      createForm.resetFields()
    } catch {
      // validation error — handled by form
    }
  }

  const handleOpenEdit = (record: AccessGroupResponse) => {
    setEditingGroup(record)
    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
    })
    setEditModalOpen(true)
  }

  const handleEdit = async () => {
    if (!editingGroup) return
    try {
      const values = await editForm.validateFields()
      await updateGroupMutation.mutateAsync({
        groupId: editingGroup.id,
        data: {
          name: values.name,
          description: values.description || null,
        },
      })
      setEditModalOpen(false)
      setEditingGroup(null)
    } catch {
      // validation error
    }
  }

  const handleCloseDetail = () => {
    setSelectedGroupId(null)
    setAddMemberModalOpen(false)
    setAddEntryModalOpen(false)
    setPendingMemberRemovals(new Set())
    setPendingMemberAdditions([])
    addMemberForm.resetFields()
  }

  const handleAddMember = async () => {
    if (!selectedGroupId) return
    try {
      const values = await addMemberForm.validateFields()
      const option = userOptions.find((u) => u.value === values.user_id)
      if (!option) return
      setPendingMemberAdditions((prev) => [
        ...prev,
        {
          user_id: option.value,
          user_email: option.email,
          user_full_name: option.full_name,
          granted_by: null,
          granted_at: new Date().toISOString(),
        },
      ])
      setAddMemberModalOpen(false)
      addMemberForm.resetFields()
    } catch {
      // validation error
    }
  }

  const handleRemoveMember = (member: AccessGroupMember) => {
    if (pendingMemberAdditions.some((m) => m.user_id === member.user_id)) {
      setPendingMemberAdditions((prev) => prev.filter((m) => m.user_id !== member.user_id))
      return
    }
    setPendingMemberRemovals((prev) => {
      const next = new Set(prev)
      if (next.has(member.user_id)) {
        next.delete(member.user_id)
      } else {
        next.add(member.user_id)
      }
      return next
    })
  }

  const handleApplyMemberChanges = async () => {
    if (!selectedGroupId || !hasMemberChanges) return
    setApplyingMembers(true)
    try {
      for (const userId of pendingMemberRemovals) {
        await removeMemberMutation.mutateAsync({ groupId: selectedGroupId, userId })
      }
      for (const member of pendingMemberAdditions) {
        await addMemberMutation.mutateAsync({ groupId: selectedGroupId, userId: member.user_id })
      }
      setPendingMemberRemovals(new Set())
      setPendingMemberAdditions([])
    } finally {
      setApplyingMembers(false)
    }
  }

  const handleCancelMemberChanges = () => {
    setPendingMemberRemovals(new Set())
    setPendingMemberAdditions([])
  }

  const handleAddEntries = async (picked: DatalensEntrySelection[]) => {
    if (!selectedGroupId) return
    setPickerLoading(true)
    try {
      for (const entry of picked) {
        await addEntryMutation.mutateAsync({
          groupId: selectedGroupId,
          data: {
            datalens_entry_id: entry.datalens_entry_id,
            entry_type: 'dash',
            entry_title: entry.entry_title || null,
          },
        })
      }
      setAddEntryModalOpen(false)
      setSelectedEntriesToAdd([])
    } finally {
      setPickerLoading(false)
    }
  }

  const handleDeleteEntry = (entry: AccessGroupEntry) => {
    if (!selectedGroupId) return
    deleteEntryMutation.mutate({ groupId: selectedGroupId, entryId: entry.id })
  }

  const handleDeactivate = (groupId: string) => {
    deactivateGroupMutation.mutate(groupId)
  }

  const handleDeploy = async () => {
    try {
      await deployMutation.mutateAsync()
      message.success('Настройки доступа синхронизированы')
    } catch {
      message.error('Ошибка синхронизации')
    }
  }

  // ── Groups table columns ──
  const columns: ColumnsType<AccessGroupResponse> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    ...(isSuperadmin
      ? [
          {
            title: 'Ключ роли',
            dataIndex: 'zitadel_role_key',
            key: 'zitadel_role_key',
            render: (v: string) => (
              <Space size={4} wrap>
                <Tag color="purple">{v}</Tag>
                {isOrgMembershipRoleKey(v) && <Tag color="cyan">org-scope</Tag>}
              </Space>
            ),
          },
        ]
      : []),
    {
      title: 'Организация',
      dataIndex: 'org_id',
      key: 'org_id',
      render: (v: string | null) =>
        v ? orgName(v) : <Tag color="blue">Областная</Tag>,
      ellipsis: true,
    },
    {
      title: 'Участники',
      dataIndex: 'member_count',
      key: 'member_count',
      width: 110,
      sorter: (a, b) => (a.member_count ?? 0) - (b.member_count ?? 0),
      render: (v: number) => (
        <Space size={4}>
          <TeamOutlined style={{ color: '#8c8c8c' }} />
          {v ?? 0}
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (v: boolean) => (
        <Badge
          status={v ? 'success' : 'default'}
          text={v ? 'Активна' : 'Неактивна'}
        />
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 280,
      render: (_: unknown, record: AccessGroupResponse) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<TeamOutlined />}
            onClick={() => navigate(`/admin/access-groups/${record.id}`)}
          >
            Управление
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleOpenEdit(record)}
          >
            Изменить
          </Button>
          {record.is_active && (
            <Popconfirm
              title="Деактивировать группу?"
              description="Сотрудники потеряют доступ через эту группу."
              onConfirm={() => handleDeactivate(record.id)}
              okText="Деактивировать"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger>
                Деактивировать
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Members table columns ──
  const memberColumns: ColumnsType<AccessGroupMember> = [
    {
      title: 'ФИО',
      dataIndex: 'user_full_name',
      key: 'user_full_name',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Email',
      dataIndex: 'user_email',
      key: 'user_email',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Добавлен',
      dataIndex: 'granted_at',
      key: 'granted_at',
      width: 160,
      render: (dt: string) => dayjs(dt).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Действие',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AccessGroupMember) => (
        <Popconfirm
          title={
            pendingMemberAdditions.some((m) => m.user_id === record.user_id)
              ? 'Убрать добавление сотрудника?'
              : pendingMemberRemovals.has(record.user_id)
                ? 'Отменить удаление сотрудника?'
                : 'Удалить сотрудника из группы?'
          }
          onConfirm={() => handleRemoveMember(record)}
          okText={
            pendingMemberAdditions.some((m) => m.user_id === record.user_id)
              ? 'Убрать'
              : pendingMemberRemovals.has(record.user_id)
                ? 'Отменить'
                : 'Удалить'
          }
          cancelText="Отмена"
          okButtonProps={{
            danger: !pendingMemberRemovals.has(record.user_id) &&
              !pendingMemberAdditions.some((m) => m.user_id === record.user_id),
          }}
        >
          <Button
            type="link"
            size="small"
            danger={
              !pendingMemberRemovals.has(record.user_id) &&
              !pendingMemberAdditions.some((m) => m.user_id === record.user_id)
            }
          >
            {pendingMemberAdditions.some((m) => m.user_id === record.user_id)
              ? 'Убрать'
              : pendingMemberRemovals.has(record.user_id)
                ? 'Отменить'
                : 'Удалить'}
          </Button>
        </Popconfirm>
      ),
    },
  ]

  // ── Entries table columns ──
  const entryColumns: ColumnsType<AccessGroupEntry> = [
    {
      title: 'Название',
      dataIndex: 'entry_title',
      key: 'entry_title',
      ellipsis: true,
      render: (v: string | null, record: AccessGroupEntry) =>
        v || extractNameFromKey(record.datalens_entry_id),
    },
    {
      title: 'Раздел',
      dataIndex: 'workbook_title',
      key: 'workbook_title',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Entry ID',
      dataIndex: 'datalens_entry_id',
      key: 'datalens_entry_id',
      width: 160,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Тип',
      dataIndex: 'entry_type',
      key: 'entry_type',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'dash' ? 'blue' : v === 'dataset' ? 'green' : 'default'}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Добавлен',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (dt: string) => dayjs(dt).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Действие',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AccessGroupEntry) => (
        <Popconfirm
          title="Удалить дашборд из группы?"
          onConfirm={() => handleDeleteEntry(record)}
          okText="Удалить"
          cancelText="Отмена"
          okButtonProps={{ danger: true }}
        >
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Группы доступа к дашбордам' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Группы доступа к дашбордам
        </Title>
        <Space>
          {isSuperadmin && (
            <Popconfirm
              title="Синхронизировать настройки доступа с DataLens BI?"
              onConfirm={handleDeploy}
              okText="Да"
              cancelText="Отмена"
            >
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                loading={deployMutation.isPending}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Синхронизировать с BI
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields()
              if (!isSuperadmin && authUser?.orgId) {
                createForm.setFieldValue('org_id', authUser.orgId)
              }
              setCreateModalOpen(true)
            }}
          >
            Создать группу
          </Button>
        </Space>
      </div>

      {/* Deploy status info */}
      {isSuperadmin && configStatus?.last_deploy_at && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Последняя публикация:{' '}
              <Text strong>{dayjs(configStatus.last_deploy_at).format('DD.MM.YYYY HH:mm')}</Text>
              {configStatus.last_deploy_by && (
                <> ({configStatus.last_deploy_by})</>
              )}
            </span>
          }
        />
      )}

      {/* Toolbar: фильтр по организации */}
      {isSuperadmin && (
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="Все организации"
            allowClear
            style={{ width: 280 }}
            options={orgOptions}
            onChange={(v) => setOrgFilter(v)}
          />
        </Space>
      )}

      {/* Таблица групп */}
      <Table
        dataSource={groups || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />

      {/* Modal: Создать группу */}
      <Modal
        title="Создать группу доступа"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalOpen(false)
          createForm.resetFields()
        }}
        confirmLoading={createGroupMutation.isPending}
        okText="Создать"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название группы' }]}
          >
            <Input placeholder="Например: Аналитики ЦКДЛ" />
          </Form.Item>
          {isSuperadmin && (
            <Form.Item
              name="zitadel_role_key"
              label="Ключ роли Zitadel"
              rules={[{ required: true, message: 'Введите ключ роли' }]}
            >
              <Input placeholder="Например: datalens_analyst" />
            </Form.Item>
          )}
          <Form.Item name="description" label="Описание">
            <Input.TextArea placeholder="Опциональное описание группы" rows={3} />
          </Form.Item>
          {isSuperadmin && (
            <Form.Item name="org_id" label="Организация">
              <Select
                allowClear
                placeholder="Областная группа (без привязки к ЛПУ)"
                options={orgOptions}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Modal: Редактировать группу */}
      <Modal
        title="Редактировать группу доступа"
        open={editModalOpen}
        onOk={handleEdit}
        onCancel={() => {
          setEditModalOpen(false)
          setEditingGroup(null)
        }}
        confirmLoading={updateGroupMutation.isPending}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: 'Введите название группы' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Drawer: Управление группой (участники + дашборды) */}
      <Drawer
        title={
          groupDetail
            ? `Управление: ${groupDetail.name}`
            : 'Управление группой'
        }
        open={!!selectedGroupId}
        onClose={handleCloseDetail}
        width={720}
        destroyOnClose
      >
        <Tabs
          defaultActiveKey="entries"
          items={[
            {
              key: 'entries',
              label: (
                <span>
                  <AppstoreOutlined /> Дашборды ({entries?.length ?? 0})
                </span>
              ),
              children: (
                <>
                  {selectedGroupIsOrgMembership ? (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Для групп вида mo&lt;код ЛПУ&gt; доступ к дашбордам не назначается"
                      description="Эти группы используются как маркер принадлежности к ЛПУ. Назначайте видимость BI-дашбордов через специализированные ACL-группы."
                    />
                  ) : (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      style={{ marginBottom: 16 }}
                      onClick={() => setAddEntryModalOpen(true)}
                    >
                      Добавить дашборд
                    </Button>
                  )}

                  <Table
                    dataSource={entries || []}
                    columns={entryColumns}
                    rowKey="id"
                    loading={entriesLoading}
                    size="small"
                    pagination={
                      (entries?.length ?? 0) > 100
                        ? { pageSize: 100, showSizeChanger: false }
                        : false
                    }
                    locale={{ emptyText: 'Нет привязанных дашбордов' }}
                  />
                </>
              ),
            },
            {
              key: 'members',
              label: (
                <span>
                  <TeamOutlined /> Участники ({groupDetail?.members?.length ?? 0})
                </span>
              ),
              children: (
                <>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 16 }}
                    onClick={() => {
                      addMemberForm.resetFields()
                      setAddMemberModalOpen(true)
                    }}
                  >
                    Добавить сотрудника
                  </Button>

                  <Space style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      disabled={!hasMemberChanges}
                      loading={applyingMembers}
                      onClick={handleApplyMemberChanges}
                    >
                      Применить
                    </Button>
                    <Button
                      disabled={!hasMemberChanges}
                      onClick={handleCancelMemberChanges}
                    >
                      Отменить
                    </Button>
                  </Space>

                  <Table
                    dataSource={mergedMembers}
                    columns={memberColumns}
                    rowKey="user_id"
                    loading={detailLoading}
                    size="small"
                    pagination={
                      mergedMembers.length > 100
                        ? { pageSize: 100, showSizeChanger: false }
                        : false
                    }
                    locale={{ emptyText: 'Нет сотрудников в группе' }}
                    rowClassName={(record) => {
                      if (pendingMemberAdditions.some((m) => m.user_id === record.user_id)) {
                        return 'pending-member-add'
                      }
                      if (pendingMemberRemovals.has(record.user_id)) {
                        return 'pending-member-remove'
                      }
                      return ''
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Drawer>

      {/* Modal: Добавить участника */}
      <Modal
        title="Добавить сотрудника в группу"
        open={addMemberModalOpen}
        onOk={handleAddMember}
        onCancel={() => {
          setAddMemberModalOpen(false)
          addMemberForm.resetFields()
        }}
        confirmLoading={addMemberMutation.isPending}
        okText="Добавить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={addMemberForm} layout="vertical">
          <Form.Item
            name="user_id"
            label="Сотрудник"
            rules={[{ required: true, message: 'Выберите сотрудника' }]}
          >
            <Select
              showSearch
              placeholder="Начните вводить ФИО или email"
              options={userOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent="Нет доступных сотрудников"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Выбор BI-объектов для группы */}
      <Modal
        title="Добавить дашборды в группу"
        open={addEntryModalOpen}
        onCancel={() => {
          setAddEntryModalOpen(false)
          setSelectedEntriesToAdd([])
        }}
        onOk={() => void handleAddEntries(selectedEntriesToAdd)}
        okText="Добавить выбранные"
        cancelText="Отмена"
        confirmLoading={pickerLoading}
        okButtonProps={{ disabled: selectedEntriesToAdd.length === 0 }}
        width={860}
        destroyOnHidden
      >
        {detailLoading || !groupDetail ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="Загрузка группы..." />
          </div>
        ) : (
          <DatalensEntryPicker
            mode="multiple"
            values={selectedEntriesToAdd.map((entry) => entry.datalens_entry_id)}
            onChangeMany={setSelectedEntriesToAdd}
            orgId={groupDetail.org_id}
            disabled={pickerLoading}
            disabledEntryIds={new Set((entries || []).map((e) => e.datalens_entry_id))}
            height={460}
          />
        )}
      </Modal>

      {/* Deploy modal removed — sync is now inline Popconfirm + message feedback */}
    </div>
  )
}
